import { ComponentObservable, componentObserver } from '../../component-observer';
import { breakpointListener } from '../../utils/breakpoint-listener.module';
import { allowScrollingOnBody, preventScrollingOnBody } from '../../utils/body-scroll.utils';
import { toastNotification } from '../toast-notification/toast-notification.module';
import { HamiltonResourceDocument, HamiltonResourceProduct, HamiltonResourceResult } from '../../api/search-api';
import { deleteItemFromFavoritesList, getAllFavorites, HamiltonResourceFavorite } from '../../api/favorites-api';
import { resourcesCenter } from '../resources-center/resources-center.module';
import { resourceTile } from '../tiles/resource-tile/resource-tile.module';
import { createHtmlElementFromString, formatText, getResourceTilePlaceholderIconForType } from '../../utils/html.utils';
import { Icon } from '../../general/icons/Icon';
import { favoritesListData } from '../../favorites-list-data.module';
import { getResourceDetailPageTypeFromResourceType } from '../../api/resources-api';
import { Param, writeToUrl } from '../../utils/url.utils';

/**
 * Initializes the favorites list elements.
 */
class FavoritesListModule implements ComponentObservable {
  /**
   * Selector of all elements that must be initialized in javascript.
   */
  componentSelector = '.favorites-list';

  /**
   * Class that hides/displays the favorites list.
   */
  visibleClass = 'favorites-list--visible';

  /**
   * Button for the resource tile that deletes the resource from the favorites.
   */
  resourceTileDeleteButtonMarkup = `<span class="tile__remove-button">${Icon.delete}</span>`;

  /**
   * Create the favorites list. Fetches the favorites list and renders it.
   * @param favoritesListElement: The root element of the favorites list.
   */
  createFavoritesList(favoritesListElement: HTMLElement): void {
    // get the favorites list data
    this.fetchFavoritesListData()
      .then((favoritesListData) => {
        // Render table
        this.renderFavoritesListWithData(favoritesListData, favoritesListElement);

        formatText(favoritesListElement);
      })
      .catch(() => {
        this.hideTriggerButton();
      });

    // Render loading state while waiting for data
    this.renderFavoritesListWithData(null, favoritesListElement);
  }

  /**
   * Retrieve the favorites list data.
   */
  fetchFavoritesListData(): Promise<HamiltonResourceFavorite[]> {
    return getAllFavorites().then((favorites: HamiltonResourceFavorite[]) => {
      return favorites;
    });
  }

  /**
   * Render the favorites list.
   * @param favoritesList: The favorites list data.
   * @param favoritesListElement: The favorites list root element.
   */
  renderFavoritesListWithData(favoritesList: HamiltonResourceFavorite[], favoritesListElement: HTMLElement): void {
    // This is the container for all the favorites lists
    const favoritesListsContentWrapper = favoritesListElement.querySelector('.favorites-list__wrapper') as HTMLElement;

    // clear the content before adding new elements (except for empty state element)
    const favoritesListEmptyState = favoritesListsContentWrapper.firstElementChild;
    favoritesListsContentWrapper.innerHTML = '';
    favoritesListsContentWrapper.appendChild(favoritesListEmptyState);

    if (favoritesList) {
      // set counter tag (number of results)
      const resultCountTag = favoritesListElement.querySelector<HTMLElement>('.tag');
      resultCountTag.innerText = `${favoritesList.length}` || '0';

      for (const resource of favoritesList) {
        let content = '';
        if (resource.type === 'catalog-articles') {
          content = resourceTile.renderTileProduct(
            resource as HamiltonResourceProduct,
            favoritesListElement.dataset,
            true
          );
        } else {
          content = resourceTile.renderTileDocument(
            resource as HamiltonResourceDocument,
            favoritesListElement.dataset,
            true
          );
        }

        // create the tile
        const resourceTileElement = createHtmlElementFromString(content);

        if (resource.type !== 'catalog-articles') {
          const imgElem = resourceTileElement.querySelector<HTMLElement>('img');
          if (imgElem) {
            imgElem.addEventListener('error', (e) => {
              const fallbackElement = createHtmlElementFromString(
                getResourceTilePlaceholderIconForType((resource as HamiltonResourceDocument).tagValue)
              );
              if (
                (resource as HamiltonResourceDocument).mimeType &&
                (resource as HamiltonResourceDocument).mimeType.toLowerCase().indexOf('ppt') > -1
              ) {
                (e.currentTarget as HTMLImageElement).src =
                  '/.resources/hamilton-theme/hamilton-medical-theme/webresources/css/images/ppt.jpg';
              } else {
                (e.currentTarget as HTMLElement).replaceWith(fallbackElement);
              }
            });
          }
        }

        // create the delete button
        this.createDeleteButton(resourceTileElement, favoritesListElement, resource);

        // delete the pin buttons
        const pinButtons = resourceTileElement.querySelectorAll('.tile__pin-button');
        for (const pinButton of pinButtons) {
          pinButton?.parentElement?.removeChild(pinButton);
        }

        // listen for clicks on the detail buttons
        this.setupDetailPageClickListenersForResourceTiles(resourceTileElement, resource);

        // finally, append the resource tile to the grid
        favoritesListsContentWrapper.appendChild(resourceTileElement);
      }

      favoritesListElement.classList.toggle('favorites-list--empty', favoritesList?.length === 0);
    } else {
      this.renderLoadingState(favoritesListsContentWrapper);
    }
  }

  /**
   * Create the delete button for a resource tile. Adds an event listener that removes the clicked item
   * from the favorites list and reloads it.
   */
  createDeleteButton(resourceTileElement: HTMLElement, root: HTMLElement, resource: HamiltonResourceFavorite): void {
    // create the button
    const deleteButton = createHtmlElementFromString(this.resourceTileDeleteButtonMarkup);
    resourceTileElement.appendChild(deleteButton);

    // add listener for clicks
    deleteButton.addEventListener('click', () => {
      deleteItemFromFavoritesList(resource.favoritesItemId).then((success) => {
        if (success) {
          // reload the favorites list, so that the deleted item disappears
          this.reloadFavoritesList(root);

          // also delete the item from the cached favorites list product IDs and update the visible pin buttons in the resource tiles
          favoritesListData.removeResourceFromCachedFavoritesList(resource.nodeid);
          // TODO: Optimization: find workaround for updating pin buttons
          resourcesCenter.resourcesList.updatePinButtonStates();
          resourcesCenter.resourcesCenterDetail?.updatePinButtonState();
        } else {
          const errorMessage = root.dataset.errorMessage;
          toastNotification.createErrorToastMessage(errorMessage);
        }
      });
    });
  }

  /**
   * Setup click listeners for the resource tiles. The view or detail buttons need to link to the detail page.
   */
  setupDetailPageClickListenersForResourceTiles(tile: HTMLElement, resource: HamiltonResourceResult): void {
    const button = tile.querySelector(`.${resourceTile.resourceTileDetailButtonClass}`);
    const title = tile.querySelector('.tile__headline');
    const detailPageType = getResourceDetailPageTypeFromResourceType(resource.type);
    if (button && detailPageType) {
      // If there exists a detail page for this type, add event listeners
      button.addEventListener('click', () => {
        this.showDetailPageForTile(resource);
      });
      title?.addEventListener('click', () => {
        this.showDetailPageForTile(resource);
      });
    }
  }

  /**
   * Navigate to the detail page of the given resource and close the favorites list.
   */
  showDetailPageForTile(resource: HamiltonResourceResult): void {
    writeToUrl([
      { param: Param.RESOURCE_DETAIL_TYPE, value: resource.type },
      { param: Param.RESOURCE_DETAIL_ID, value: resource.nodeid }
    ]);
    this.closeFavoritesList();
  }

  /**
   * Render skeleton rows in the given container.
   */
  renderLoadingState(listsContainer: HTMLElement): void {
    listsContainer.innerHTML +=
      '<span class="favorites-list__skeleton"></span><span class="favorites-list__skeleton"></span>';
  }

  /**
   * Reloads the favorites list.
   */
  reloadFavoritesList(root: HTMLElement): void {
    this.createFavoritesList(root);
  }

  /**
   * Initialize all favorites lists on the current page.
   * @param observe Boolean if the favoritesListModule should listen for changes in the DOM and initialize dynamically
   *   added elements
   */
  initializeAll(observe: boolean): void {
    const allComponents = document.querySelectorAll(this.componentSelector);
    for (let i = 0; i < allComponents.length; i++) {
      this.initialize(allComponents[i] as HTMLElement);
    }

    if (observe) {
      this.startListening();
    }
  }

  /**
   * Listen for changes in DOM and initialize elements when new ones appear
   */
  startListening(): void {
    componentObserver.subscribeListener(this);
  }

  /**
   * Set the selected state of the trigger button. Adds the primary state (blue) of the button
   * that shows that the favorites list is currently open.
   * @param triggerButton: The trigger button HTML-Element
   * @param selected: If the button should be selected.
   */
  setTriggerButtonSelectedState(triggerButton: HTMLElement, selected: boolean): void {
    triggerButton?.classList.toggle('button--primary', selected);
  }

  /**
   * Hides button that open the wish list.
   */
  hideTriggerButton(): void {
    document.documentElement.classList.add('user-logged-out');
    document.documentElement.classList.remove('is-webshop-user');
  }

  /**
   * Deselect the trigger button. Removes the primary state (blue) after the favorites list
   * was closed.
   */
  deselectTriggerButton(): void {
    const triggerButton = document.querySelector('#header-button-favorites-list');
    triggerButton?.classList.remove('button--primary');
  }

  /**
   * Show/hide the favorites list when the trigger button was clicked.
   */
  toggleFavoritesListVisibleState(root: HTMLElement): void {
    root.classList.toggle(this.visibleClass);
    // set aria attribute
    const isHidden = !root.classList.contains(this.visibleClass);
    root.setAttribute('aria-hidden', isHidden.toString());
    if (!isHidden) {
      // reload the favorites list before opening it
      this.reloadFavoritesList(root);
      preventScrollingOnBody();
    } else {
      // deselect all favorites list buttons
      this.deselectTriggerButton();
      // remove the no scroll classes
      allowScrollingOnBody();
    }
  }

  /**
   * Close an open favoritesList.
   */
  closeFavoritesList(): void {
    // deselect the trigger (favorites) button
    const favoritesListElement = document.querySelector('.favorites-list');
    const favoritesListId = favoritesListElement.getAttribute('id');
    const triggerButton = document.querySelector<HTMLElement>(`[data-favorites-list-id='${favoritesListId}']`);
    this.setTriggerButtonSelectedState(triggerButton, false);

    favoritesListElement.classList.remove(this.visibleClass);

    // remove scroll classes on body
    allowScrollingOnBody();
  }

  /**
   * Initialize open favoritesList trigger (button). Opens the sidebar on click only on mobile.
   * @param root The favorites list root element
   */
  initializeOpenFavoritesListTrigger(root: HTMLElement): void {
    const favoritesListId = root.getAttribute('id');
    const triggerButton = document.querySelector<HTMLElement>(`[data-favorites-list-id='${favoritesListId}']`);
    if (triggerButton) {
      triggerButton.addEventListener('click', () => {
        this.toggleFavoritesListVisibleState(root);

        const isVisible = root.classList.contains(this.visibleClass);
        this.setTriggerButtonSelectedState(triggerButton, isVisible);
      });
    }
  }

  /**
   * Open the favorites list if it is currently closed. Reloads the items in the favorites list.
   */
  openFavoritesList(): void {
    const favoritesListElement = document.querySelector<HTMLElement>('.favorites-list');
    const isHidden = !favoritesListElement.classList.contains(this.visibleClass);

    if (isHidden) {
      this.toggleFavoritesListVisibleState(favoritesListElement);
    }
  }

  /**
   * Initialize the given favorites list element.
   * @param root The favorites list root element that should be initialized
   */
  initialize(root: HTMLElement): void {
    componentObserver.markElementAsInitialized(this.componentSelector, root);

    // listen for clicks on the close button
    root
      .querySelector('.favorites-list__close-button')
      .addEventListener('click', this.toggleFavoritesListVisibleState.bind(this, root), false);

    // Add ESC key listener to close favorites list
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && root.classList.contains(this.visibleClass)) {
        this.closeFavoritesList();
      }
    };

    // Add the event listener to document when favorites list becomes visible
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          const target = mutation.target as HTMLElement;
          if (target.classList.contains(this.visibleClass)) {
            document.addEventListener('keydown', handleEscKey);
          } else {
            document.removeEventListener('keydown', handleEscKey);
          }
        }
      });
    });

    observer.observe(root, {
      attributes: true,
      attributeFilter: ['class']
    });


    // close the favorites list when breakpoint changes from mobile -> desktop and the other way round
    breakpointListener.listenForBreakpointChangeBetweenMobileAndDesktop(() => {
      this.closeFavoritesList();
    });

    this.initializeOpenFavoritesListTrigger(root);
  }
}

const favoritesListModule = new FavoritesListModule();
export { favoritesListModule as favoritesList };
